import { FC } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  LabelList,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useThemeColors } from "@/hooks";

import { DistributionBin, SAMPLE_SIZE } from "./voice";

/* ── Types ─────────────────────────────────────── */

export interface DistributionChartProps {
  bins: DistributionBin[];
  /** Index of the bin to emphasise (e.g. the selected shelf price). */
  highlight?: number;
  /** Height of one bar row, in px. */
  rowHeight?: number;
}

/* ── Constants ─────────────────────────────────── */

const GRID_STROKE = "hsl(220,14%,93%)";
const LABEL_WIDTH = 118;
const MUTED_FILL = "#ddd6fe"; // violet-200
const TICK_DARK = "hsl(222,20%,16%)";
const TICK_MUTED = "hsl(220,10%,46%)";

/* ── Component ─────────────────────────────────── */

/** Share of respondents per answer, in the product's horizontal-bar style. */
const DistributionChart: FC<DistributionChartProps> = ({
  bins,
  highlight,
  rowHeight = 38,
}) => {
  const themeColors = useThemeColors();
  const data = bins.map((bin) => ({
    count: Math.round((bin.share / 100) * SAMPLE_SIZE),
    name: bin.label,
    value: bin.share,
  }));
  const max = Math.max(...data.map((d) => d.value), 10);

  return (
    <div style={{ minWidth: 0, width: "100%" }}>
      <ResponsiveContainer
        height={data.length * rowHeight + 8}
        minWidth={0}
        width="100%"
      >
        <RechartsBarChart
          barCategoryGap={8}
          data={data}
          layout="vertical"
          margin={{ bottom: 0, left: 0, right: 44, top: 4 }}
        >
          <CartesianGrid
            horizontal={false}
            stroke={GRID_STROKE}
            strokeDasharray="3 3"
          />
          <XAxis domain={[0, Math.ceil(max / 10) * 10]} hide type="number" />
          <YAxis
            axisLine={false}
            dataKey="name"
            interval={0}
            tick={{ fill: TICK_DARK, fontSize: 11, fontWeight: 500 }}
            tickLine={false}
            type="category"
            width={LABEL_WIDTH}
          />
          <Tooltip
            content={<BinTooltip primary={themeColors.primary} />}
            cursor={{ fill: "hsl(220,14%,97%)" }}
          />
          <Bar
            animationDuration={500}
            animationEasing="ease-out"
            dataKey="value"
            maxBarSize={22}
            radius={[0, 4, 4, 0]}
          >
            {data.map((_, i) => (
              <Cell
                fill={
                  highlight === undefined || highlight === i
                    ? themeColors.primary
                    : MUTED_FILL
                }
                key={i}
              />
            ))}
            <LabelList
              dataKey="value"
              formatter={(v: unknown) => `${Math.round(Number(v))}%`}
              offset={8}
              position="right"
              style={{ fill: TICK_MUTED, fontSize: 11, fontWeight: 500 }}
            />
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Subcomponents ─────────────────────────────── */

const BinTooltip: FC<{
  active?: boolean;
  payload?: { payload: { count: number; name: string; value: number } }[];
  primary: string;
}> = ({ active, payload, primary }) => {
  if (!active || !payload?.length) return null;
  const { count, name, value } = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: TICK_DARK,
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {name}
      </p>
      <p style={{ color: primary, fontSize: 12, margin: "2px 0 0" }}>
        {Math.round(value)}% · N={count}
      </p>
    </div>
  );
};

export default DistributionChart;
